import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Card, Col, Container, Row } from 'react-bootstrap';
import LiveTickerBar from '../../../components/live-ticker-bar/liveTickerBar';
import StackedBarChart from '../../../components/charts/stackedBarChart/stackedBartChart';
import DataTableComponent from '../../../components/dataTables/dataTable';
import '../../market/market.scss'
import { useDispatch, useSelector } from "react-redux";
import PeriodSelection from '../../../components/selections/periodSelection/periodSelection';
import MultipleDatePicker from '../../../components/pickers/multipleDatePicker';
import moment from 'moment/moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendars, faChartSimpleHorizontal, faPencil, faTable } from '@fortawesome/pro-solid-svg-icons';
import lodash from 'lodash';

import { MarketSummaryServiceApi } from "../../../services/marketQuery/api";
import { Configuration } from "../../../services/marketQuery/configuration"
import NumberFormatter from '../../../formatters/NumberFormatter'
import DateFormatter from '../../../formatters/DateFormatter'
import { APIControl } from '../../../helpers/APIControl';
import { useTranslation } from "react-i18next";
import Spinner from '../../../components/spinner';

const GlobalIndices = () => {
    const { t } = useTranslation();
    const numberFormatter = new NumberFormatter();
    const dateFormatter = new DateFormatter();
    const marketApi = new MarketSummaryServiceApi(new Configuration({ basePath: process.env.REACT_APP_MARKET_QUERY_API_BASE_PATH }), undefined, APIControl(process.env.REACT_APP_MARKET_QUERY_API_BASE_PATH));
    const user = useSelector((state) => state.auth?.user);
    const token = localStorage.getItem('token');
    const accountId = useSelector((state) => state.auth?.accountId);
    const toast = useRef(null);
    const [activePeriod, setActivePeriod] = useState(t('period.daily'));
    const [firstDate, setfirstDate] = useState(moment().subtract(1, 'months').toDate());
    const [lastDate, setlastDate] = useState(new Date());
    const [showTable, setShowTable] = useState(false);
    const [marketGlobalIndicesTableColumnsData, setMarketGlobalIndicesTableColumnsData] = useState([]);
    const [marketGlobalIndicesServiceData, setMarketGlobalIndicesServiceData] = useState([]);
    const [globalIndicesTableChartData, setGlobalIndicesTableChartData] = useState([]);
    const [isLoadingChart, setIsLoadingChart] = useState(true);
    const [isLoadingTable, setIsLoadingTable] = useState(true);
    const [isDateRangeActive, setIsDateRangeActive] = useState(false);

    const periods = [t('period.daily'), t('period.weekly'), t('period.monthly'), t('period.ytd'), t('period.yearly')];
    const globalIndicesTableDataColumnsChartView = [
        { id: 1, field: 'name', displayName: 'Endeks', sortable: false, header: t('tableHeader.index'), isVisible: true, width: '60%', align: 'left' },
        { id: 2, field: 'lastDay', displayName: 'Tarih', header: t('tableHeader.date'), isVisible: true, width: '20%', align: 'center' },
        { id: 3, field: 'lastValue', displayName: 'Son', header: t('tableHeader.last'), isVisible: true, width: '20%', align: 'center' },
    ]
    const globalIndicesTableColumnsData = [
        { id: 1, field: 'name', displayName: 'Endeks', header: t('tableHeader.index'), isVisible: true, width: '30%', align: 'left', clickable: false },
        { id: 2, field: 'lastDay', displayName: 'Tarih', header: t('tableHeader.date'), isVisible: true, width: '20%', align: 'center' },
        { id: 3, field: 'lastValue', displayName: 'Son', header: t('tableHeader.last'), isVisible: true, width: '25%', align: 'center' },
        { id: 4, field: 'rocDailyChange', displayName: 'rocDaily', header: t('tableHeader.change'), template: '', isVisible: false, width: '33%', align: 'center' },
        { id: 5, field: 'rocWeeklyChange', displayName: 'rocWeekly', header: t('tableHeader.change'), template: '', isVisible: true, width: '33%', align: 'center' },
        { id: 6, field: 'rocMonthlyChange', displayName: 'rocMonthly', header: t('tableHeader.change'), template: '', isVisible: false, width: '33%', align: 'center' },
        { id: 7, field: 'rocYtdChange', displayName: 'rocYtd', header: t('tableHeader.change'), template: '', isVisible: false, width: '33%', align: 'center' },
        { id: 8, field: 'rocAnnualyChange', displayName: 'rocAnnualy', header: t('tableHeader.change'), template: '', isVisible: false, width: '33%', align: 'center' },
        { id: 9, field: 'rocBetweenDateChange', displayName: 'rocBetweenDate', header: t('tableHeader.change'), template: '', isVisible: false, width: '33%', align: 'center' },
    ]

    useEffect(() => {
        getGlobalIndexData();

        setMarketGlobalIndicesTableColumnsData(globalIndicesTableColumnsData);
    }, []);

    // Add a new useEffect to handle period changes
    useEffect(() => {
        // Skip on initial render
        if (!globalIndicesTableChartData.length) return;

        // If we're in date range mode and switching to standard period, we need to refetch the data
        if (isDateRangeActive && activePeriod !== 'Tarih Aralığı') {
            // Set flag to false since we're leaving date range mode
            setIsDateRangeActive(false);
            // Fetch standard period data
            getGlobalIndexData();
            return;
        }

        // Update columns and sort data based on the selected period
        if (activePeriod === t('period.daily')) {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocDailyChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;
                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocDailyRaw;
                });
                return lodash.orderBy(newData, ['rocDailyRaw'], ['desc']);
            });
        }
        else if (activePeriod === t('period.weekly')) {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocWeeklyChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;
                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocWeeklyRaw;
                });
                return lodash.orderBy(newData, ['rocWeeklyRaw'], ['desc']);
            });
        }
        else if (activePeriod === t('period.monthly')) {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocMonthlyChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;

                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocMonthlyRaw;
                });
                return lodash.orderBy(newData, ['rocMonthlyRaw'], ['desc']);
            });
        }
        else if (activePeriod === t('period.ytd')) {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocYtdChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;
                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocYtdRaw;
                });
                return lodash.orderBy(newData, ['rocYtdRaw'], ['desc']);
            });
        }
        else if (activePeriod === t('period.yearly')) {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocAnnualyChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;
                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocAnnualyRaw;
                });
                return lodash.orderBy(newData, ['rocAnnualyRaw'], ['desc']);
            });
        }
        else if (activePeriod === 'Tarih Aralığı') {
            setMarketGlobalIndicesTableColumnsData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    if (obj.field !== 'rocBetweenDateChange' && obj.field !== 'code' && obj.field !== 'name' && obj.field !== 'lastDay' && obj.field !== 'lastValue') {
                        obj.isVisible = false;
                    }
                    else {
                        obj.isVisible = true;
                    }
                });
                return newData;
            });

            setGlobalIndicesTableChartData((prevData) => {
                const newData = [...prevData];
                prevData.forEach(obj => {
                    obj.summaryChartValue = obj.rocBetweenDateRaw;
                });
                return lodash.orderBy(newData, ['rocBetweenDateRaw'], ['desc']);
            });
        }
    }, [activePeriod, globalIndicesTableChartData.length, isDateRangeActive, t]);

    //#region FUNCTIONS

    const clickPeriod = (period) => {
        setActivePeriod(period);

        // If switching to date range mode directly via button click
        if (period === 'Tarih Aralığı') {
            setIsDateRangeActive(true);
            // Get the first and last date from state
            const firstDateInt = dateFormatter.dateToInt(firstDate);
            const lastDateInt = dateFormatter.dateToInt(lastDate);
            getMarketSummaryBetweenDateData(firstDateInt, lastDateInt);
        }
    }

    const onClickViewChange = () => {
        setShowTable(!showTable);
    }

    //#endregion FUNCTIONS

    //#region  DATE PICKER 


    const selectedDate = (firstDate, lastDate) => {
        try {
            firstDate = dateFormatter.dateToInt(firstDate);
            lastDate = dateFormatter.dateToInt(lastDate);

            getMarketSummaryBetweenDateData(firstDate, lastDate);
            setIsDateRangeActive(true); // Set flag when moving to date range mode
            setActivePeriod('Tarih Aralığı');

        } catch (error) {
            console.log(error);
        }
    }

    //#endregion DATE PICKER 

    //#region API CALLS

    //#region GLOBALINDEX

    const getGlobalIndexData = () => {
        setIsLoadingChart(true);
        setIsLoadingTable(true);

        const token = localStorage.getItem('token');

        marketApi.marketMarketsummaryFetchGet(token, true).then((res) => {
            const data = res.data.marketSummaries;

            // Determine which field to sort by based on activePeriod
            let sortField = 'rocDaily'; // default

            if (activePeriod === t('period.daily')) {
                sortField = 'rocDaily';
            } else if (activePeriod === t('period.weekly')) {
                sortField = 'rocWeekly';
            } else if (activePeriod === t('period.monthly')) {
                sortField = 'rocMonthly';
            } else if (activePeriod === t('period.ytd')) {
                sortField = 'rocYtd';
            } else if (activePeriod === t('period.yearly')) {
                sortField = 'rocAnnualy';
            }

            // Sort data in descending order using lodash with the appropriate field
            const sortedData = lodash.orderBy(data, [sortField], ['desc']);
            setMarketGlobalIndicesServiceData(sortedData);

            for (let index = 0; index < sortedData.length; index++) {

                const element = sortedData[index];
                element.rocDailyRaw = element.rocDaily;
                element.rocWeeklyRaw = element.rocWeekly;
                element.rocMonthlyRaw = element.rocMonthly;
                element.rocYtdRaw = element.rocYtd;
                element.rocAnnualyRaw = element.rocAnnualy;
                element.date = dateFormatter.intToDate(element.lastDay);
                element.lastDay = dateFormatter.intToDate(element.lastDay);

                if (activePeriod == t('period.daily')) { //açılışta günlük seçili gelsin
                    element.summaryChartValue = element.rocDailyRaw;
                }
                else if (activePeriod == t('period.weekly')) {
                    element.summaryChartValue = element.rocWeeklyRaw;
                }
                else if (activePeriod == t('period.monthly')) {
                    element.summaryChartValue = element.rocMonthlyRaw;
                }
                else if (activePeriod == t('period.ytd')) {
                    element.summaryChartValue = element.rocYtdRaw;
                }
                else if (activePeriod == t('period.yearly')) {
                    element.summaryChartValue = element.rocAnnualyRaw;
                }

                element.lastValue = numberFormatter.Double(element.lastValue, 2);
                element.rocDailyChange = numberFormatter.Double(element.rocDaily, 2);
                element.rocWeeklyChange = numberFormatter.Double(element.rocWeekly, 2);
                element.rocMonthlyChange = numberFormatter.Double(element.rocMonthly, 2);
                element.rocYtdChange = numberFormatter.Double(element.rocYtd, 2);
                element.rocAnnualyChange = numberFormatter.Double(element.rocAnnualy, 2);
            }
            setGlobalIndicesTableChartData(sortedData);
            setIsLoadingChart(false);
            setIsLoadingTable(false);
        }).catch((err) => {
            setIsLoadingChart(false);
            setIsLoadingTable(false);
        });
    }

    //#endregion GLOBALINDEX


    //#region GLOBALINDEX BETWEEN DATES
    const getMarketSummaryBetweenDateData = (date1, date2) => {
        setIsLoadingChart(true);
        setIsLoadingTable(true);
        marketApi.marketMarketsummarydateFetchGet(token, true, date1, date2).then((res) => {
            const data = res.data.marketSummariesDate;

            // Sort data in descending order by rocBetweenDate
            const sortedData = lodash.orderBy(data, ['rocBetweenDate'], ['desc']);

            setMarketGlobalIndicesServiceData(sortedData);

            for (let index = 0; index < sortedData.length; index++) {
                const element = sortedData[index];
                element.rocBetweenDateRaw = element.rocBetweenDate;
                element.rocBetweenDateChange = numberFormatter.Double(element.rocBetweenDate, 2);
                element.lastValue = numberFormatter.Double(element.lastValue, 2);
                element.date = dateFormatter.intToDate(element.lastDay);
                element.lastDay = dateFormatter.intToDate(element.lastDay);
                element.summaryChartValue = element.rocBetweenDateRaw;
            }

            setGlobalIndicesTableChartData(sortedData);
            setIsLoadingChart(false);
            setIsLoadingTable(false);
        }).catch((err) => {
            setIsLoadingChart(false);
            setIsLoadingTable(false);
        });
    }
    //#endregion GLOBALINDEX BETWEEN DATES


    //#endregion API CALLS

    const calcChartHeight = () => {
        let height = 0;
        if (globalIndicesTableChartData && globalIndicesTableChartData.length > 0) {
            // Ensure minimum height for proper rendering even with small datasets
            height = Math.max(globalIndicesTableChartData.length * 36, 200);
        } else {
            // Default height if no data
            height = 300;
        }
        return height + 12 + 'px';
    }

    const customSort = (e) => {
        console.log(e);
    }


    return (
        <div className='global-indices-screen'>
            <Toast ref={toast} position="bottom-right" />

            <Container>
                <Row>
                    <Col>
                        <LiveTickerBar />
                    </Col>
                </Row>
            </Container>

            <div className='options-row m-b-20 m-t-15'>
                <PeriodSelection periods={periods} activePeriod={activePeriod} clickPeriod={clickPeriod} />
                <MultipleDatePicker selectedDate={selectedDate} firstDate={firstDate} lastDate={lastDate} />

                <div className='table-chart-icon-bg' onClick={onClickViewChange}>
                    {!showTable ? <FontAwesomeIcon className='table-chart-icon' icon={faTable} /> : <FontAwesomeIcon className='table-chart-icon' icon={faChartSimpleHorizontal} />}
                </div>
            </div>

            {
                showTable &&
                <div className='table-chart-view global-indices-table'>
                    {isLoadingTable && <Spinner height="300px" />}
                    {!isLoadingTable && (
                        <DataTableComponent columnsProp={marketGlobalIndicesTableColumnsData} dataProp={globalIndicesTableChartData} columnsSelect={false} search={false} scrollHeight={'100%'} customSort={true} onSort={(e) => customSort(e)} />
                    )}
                </div>
            }

            {
                !showTable &&
                <>

                    <Container className='chart-view'>
                        <Row>
                            <Col md='5' className='p-0 chart-view-table' >
                                {isLoadingChart && <Spinner height="300px" />}
                                {!isLoadingChart && (
                                    <DataTableComponent columnsProp={globalIndicesTableDataColumnsChartView} dataProp={globalIndicesTableChartData} columnsSelect={false} search={false} scrollHeight={'100%'} />
                                )}
                            </Col>

                            <Col md='7' className='p-0' style={{ marginTop: '40px', position: 'relative' }}>
                                {isLoadingChart && <Spinner height="100%" />}
                                {!isLoadingChart && (
                                    <StackedBarChart 
                                        data={globalIndicesTableChartData} 
                                        valueXField={'summaryChartValue'} 
                                        categoryYField={'code'} 
                                        hideLabel={false} 
                                        selectedPeriod={activePeriod} 
                                        height={calcChartHeight()} 
                                        onReload={getGlobalIndexData} 
                                    />
                                )}
                            </Col>

                        </Row>
                    </Container>
                </>
            }
        </div>
    )
}

export default GlobalIndices;
